import {
  BOOKING_COMPLETED,
  BOOKING_EXPIRED,
  BOOKING_RESET,
  CHANGE_CURRENT_STEP,
  CHANGE_LOCALE,
  CHANGE_PAYMENT_METHOD,
  CHANGE_PAYMENT_SPLIT_OPTION,
  CHANGE_PREPAY_STATUS,
  CHANGE_RECURRING_PAYMENT_MODE,
  CHANGE_SECONDARY_PAYMENT_METHOD,
  CLOSE_CART,
  CLOSE_CHECKOUT,
  CLOSE_DETAILS,
  CLOSE_PROCESSING_SPINNER,
  CLOSE_RESERVATION,
  COMPONENT_LOADED,
  DATA_FORM_FETCH_END,
  DATA_FORM_FETCH_START,
  DATA_FORM_SEND_END,
  DATA_FORM_SEND_START,
  FETCH_CART,
  FETCH_CUSTOMER,
  FETCH_ORDER_DETAILS,
  FETCH_STEPS,
  FETCH_SYSTEM,
  INIT_PAYMENT,
  NAVIGATE_TO_STEP_END,
  NAVIGATE_TO_STEP_START,
  OPEN_CART,
  OPEN_CHECKOUT,
  OPEN_DETAILS,
  OPEN_GALLERY,
  OPEN_PROCESSING_SPINNER,
  OPEN_RESERVATION,
  PAYMENT_CANCELED,
  RE_INIT,
  REMOVE_ITEM_FROM_CART,
  REQUIRE_INIT,
  STEP_LOADED,
  STORE_BILLING_VALUES,
} from './actions';
import { App, GiftCard, SplitOption } from './types';
import { getQueryParam, historyReplaceStateQueryParams, removeRentwareQueryParams } from '../utils/utils';
import { AmountToPay, Price, SplitOptionType } from '../types/types';
import { getMainConfig } from '../config';
import { hookCheckoutSuccess, hookInitiateCheckout, hookStoreBillingData } from '../utils/tracking/hooks';
import { getLocaleWithCountryCode, getLocaleWithReasonableFallback } from '../utils/language';

const initialState: App = {
  initialized: false,
  bookingId: getQueryParam('rentware-order-id') || getStoredBookingId(),
  bookingCompleted: false,
  bookingExpired: false,
  customer: null,
  expandCart: false,
  currentStep: null,
  leadStep: null,
  loadedComponents: {},
  loadedSteps: {},
  locale: getLocale(),
  systemDefaultLanguage: null,
  systemLanguages: [],
  configLocale: getLocale(),
  showCheckout: false,
  showProcessingSpinner: false,
  showDetailsForId: '',
  showReservationForId: '',
  showGalleryForId: '',
  showDetailsToCalendar: false,
  paymentFailed: false,
  paymentMethod: null,
  recurringPaymentMode: false,
  paymentMethodRecurring: false,
  secondaryPaymentMethod: null,
  paymentSplitOption: null,
  prepayStatus: null,
  amountToPay: null,
  accessToken: null,
  billingStepValues: null,
  appLoaded: false,
  currency: null,
  navigateToStepInProgress: false,
  dataFormFetchingInProgress: false,
  dataFormSendingInProgress: false,
};

export function appReducer(state = initialState, action: any): App {
  const currency = state.currency || 'EUR';
  switch (action.type) {
    case REQUIRE_INIT: {
      return { ...state, initialized: true, accessToken: action.payload?.accessToken };
    }
    case RE_INIT: {
      return { ...state, accessToken: action.payload?.accessToken };
    }
    case FETCH_SYSTEM: {
      const systemDefaultLanguage = action.payload.defaultLanguage;
      const systemLanguages = action.payload.languages;
      const currency = action.payload.unitConfig?.currency || 'EUR';

      // check if locale is already set (could be set by window.RENTWARE_LANGUAGE)
      const locale = getLocaleWithReasonableFallback(state?.locale, systemLanguages, systemDefaultLanguage);

      return {
        ...state,
        locale: locale,
        configLocale: locale,
        appLoaded: true,
        currency: currency,
        systemDefaultLanguage: systemDefaultLanguage,
        systemLanguages: systemLanguages,
      };
    }
    case BOOKING_RESET: {
      removeStoredBookingId();

      // remove params like rentware-order-id so that it is not affecting logic anymore
      historyReplaceStateQueryParams(removeRentwareQueryParams(new URLSearchParams(window.location.search)));

      return {
        ...state,
        bookingId: null,
        bookingCompleted: false,
        bookingExpired: false,
        customer: null,
        currentStep: null,
        showCheckout: false,
        showProcessingSpinner: false,
        paymentFailed: false,
        paymentMethod: null,
        secondaryPaymentMethod: null,
        paymentSplitOption: null,
        amountToPay: null,
        recurringPaymentMode: false,
      };
    }
    case BOOKING_COMPLETED: {
      hookCheckoutSuccess(getStoredBookingId(), action.payload, currency);

      removeStoredBookingId();

      // remove params like rentware-order-id so that it is not affecting logic anymore
      historyReplaceStateQueryParams(removeRentwareQueryParams(new URLSearchParams(window.location.search)));

      return {
        ...state,
        bookingCompleted: true,
        showCheckout: true,
        paymentFailed: false,
        showProcessingSpinner: false,
      };
    }

    case PAYMENT_CANCELED: {
      historyReplaceStateQueryParams(removeRentwareQueryParams(new URLSearchParams(window.location.search)));

      return {
        ...state,
        showCheckout: true,
        showProcessingSpinner: false,
        currentStep: action.payload.paymentStep ?? state.currentStep,
        paymentFailed: true,
      };
    }

    case FETCH_STEPS: {
      const steps = action.payload.steps;
      const zeroStep = steps?.[0];

      if (!state.currentStep) {
        return { ...state, currentStep: zeroStep, leadStep: zeroStep };
      }

      return { ...state };
    }
    case CHANGE_CURRENT_STEP: {
      return { ...state, currentStep: {...action.payload, visitContext: action.payload.context}, paymentFailed: false };
    }
    case CHANGE_LOCALE: {
      const locale = getLocaleWithReasonableFallback(action.payload, state.systemLanguages, state.systemDefaultLanguage);
      return { ...state, locale: locale };
    }
    case OPEN_CHECKOUT: {
      document.body.style.overflowY = 'hidden';

      hookInitiateCheckout(state.bookingId, state);

      // Enable navigation prompt
      window.onbeforeunload = function () {
        return true;
      };

      return { ...state, showCheckout: true, showDetailsForId: '', expandCart: false, showReservationForId: null };
    }
    case CLOSE_CHECKOUT: {
      document.body.style.overflowY = null;

      // Remove navigation prompt
      window.onbeforeunload = undefined;

      return { ...state, showCheckout: false, paymentFailed: false, expandCart: false, currentStep: state.leadStep };
    }
    case OPEN_PROCESSING_SPINNER: {
      return { ...state, showProcessingSpinner: true, processingSpinnerContent: action.payload };
    }
    case CLOSE_PROCESSING_SPINNER: {
      return { ...state, showProcessingSpinner: false, processingSpinnerContent: null };
    }
    case OPEN_DETAILS: {
      document.body.style.overflowY = 'hidden';

      // Enable navigation prompt
      window.onbeforeunload = function () {
        return true;
      };

      return {
        ...state,
        showDetailsForId: action.payload.id,
        showDetailsToCalendar: action.payload.scrollToCalendar,
        showGalleryForId: '',
      };
    }
    case CLOSE_DETAILS: {
      if (!state.showCheckout) {
        document.body.style.overflowY = null;
        window.onbeforeunload = undefined;
        // Remove navigation prompt
      }

      return { ...state, showDetailsForId: '', showGalleryForId: '' };
    }
    case OPEN_RESERVATION: {
      document.body.style.overflowY = 'hidden';

      // Enable navigation prompt
      window.onbeforeunload = function () {
        return true;
      };

      return {
        ...state,
        showReservationForId: action.payload,
      };
    }
    case CLOSE_RESERVATION: {
      document.body.style.overflowY = null;

      // Remove navigation prompt
      window.onbeforeunload = undefined;

      return { ...state, showReservationForId: '' };
    }
    case OPEN_GALLERY: {
      document.body.style.overflowY = 'hidden';

      // Enable navigation prompt
      window.onbeforeunload = function () {
        return true;
      };

      return {
        ...state,
        showGalleryForId: action.payload.id,
        showDetailsForId: '',
      };
    }
    case OPEN_CART: {
      return { ...state, expandCart: true };
    }
    case CLOSE_CART: {
      return { ...state, expandCart: false };
    }
    case REMOVE_ITEM_FROM_CART: {
      // in case all items were removed from cart reset to initial
      if (action.payload.totalQtyInCart <= 0) {
        removeStoredBookingId();

        document.body.style.overflowY = null; // cleaning checkout
        window.onbeforeunload = undefined; // cleaning checkout

        return {
          ...state,
          bookingId: null,
          currentStep: null,
          bookingExpired: false,
          bookingCompleted: false,
          showCheckout: false, // cleaning checkout
          showProcessingSpinner: false,
          paymentFailed: false, // cleaning checkout
          recurringPaymentMode: false,
        };
      }

      return state;
    }
    case FETCH_ORDER_DETAILS: {
      // remember order reference so it can survive refreshing the browser
      storeBookingId(action.payload.id);
      if (action.payload.isExpired) {
        return { ...state, bookingExpired: true };
      }

      return state;
    }
    case BOOKING_EXPIRED: {
      document.body.style.overflowY = null; // cleaning checkout
      window.onbeforeunload = undefined; // cleaning checkout

      // expand cart as this is the only place when we show message about expired cart
      return { ...state, bookingExpired: true, expandCart: true, recurringPaymentMode: false };
    }
    case FETCH_CUSTOMER: {
      return { ...state, customer: action.payload };
    }
    case CHANGE_PAYMENT_METHOD: {
      return { ...state, paymentMethod: action.payload, paymentMethodRecurring: !!action.payload.isRecurring };
    }
    case CHANGE_PREPAY_STATUS: {
      return { ...state, prepayStatus: action.payload };
    }
    case CHANGE_SECONDARY_PAYMENT_METHOD: {
      return { ...state, secondaryPaymentMethod: action.payload };
    }
    case CHANGE_PAYMENT_SPLIT_OPTION: {
      return { ...state, paymentSplitOption: action.payload };
    }
    case CHANGE_RECURRING_PAYMENT_MODE: {
      return { ...state, recurringPaymentMode: action.payload };
    }
    case COMPONENT_LOADED: {
      return { ...state, loadedComponents: { ...state.loadedComponents, [action.payload]: true } };
    }
    case STEP_LOADED: {
      if (!state.loadedSteps?.[action.payload]) {
        return { ...state, loadedSteps: { ...state.loadedSteps, [action.payload]: true } };
      }

      return state;
    }
    case FETCH_CART: {
      return {
        ...state,
        amountToPay: calcAmountToPay(state.paymentSplitOption, action.payload.discountedPrice, action.payload.depositPrice, action.payload.giftCard),
      };
    }
    case STORE_BILLING_VALUES: {
      hookStoreBillingData(action.payload, state);
      return { ...state, billingStepValues: action.payload };
    }
    case INIT_PAYMENT: {
      if (action.payload?.methodsRecurring?.length === 0) {
        return {
          ...state,
          recurringPaymentMode: false, // disable recurring mode if was selected before but now impossible to use
        };
      }

      return state;
    }
    case NAVIGATE_TO_STEP_START: {
      return {
        ...state,
        navigateToStepInProgress: true,
      };
    }
    case NAVIGATE_TO_STEP_END: {
      return {
        ...state,
        navigateToStepInProgress: false,
      };
    }
    case DATA_FORM_FETCH_START: {
      return {
        ...state,
        dataFormFetchingInProgress: true,
      };
    }
    case DATA_FORM_FETCH_END: {
      return {
        ...state,
        dataFormFetchingInProgress: false,
      };
    }
    case DATA_FORM_SEND_START: {
      return {
        ...state,
        dataFormSendingInProgress: true,
      };
    }
    case DATA_FORM_SEND_END: {
      return {
        ...state,
        dataFormSendingInProgress: false,
      };
    }

    default:
      return state;
  }
}

function calcAmountToPay(paymentSplitOption: SplitOption, totalPrice: Price, depositPrice: Price, giftCard: GiftCard): AmountToPay {
  let amount = totalPrice.gross;
  // numbers are not parsed, because rentware-total-price does it
  if (!!paymentSplitOption) {
    if (paymentSplitOption.type === SplitOptionType.Percentage) {
      const percentage = paymentSplitOption.value;

      if (paymentSplitOption.excludeDeposit) {
        amount = ((totalPrice.gross - (depositPrice?.gross || 0)) * percentage) / 100;
      } else {
        amount = (totalPrice.gross * percentage) / 100;
      }
      // amount must be in cents but can happen that percentage gives decimal, so round to int
      amount = Math.round(amount);
    } else if (paymentSplitOption.type === SplitOptionType.Amount) {
      amount = paymentSplitOption.value * 100;
    } else if (paymentSplitOption.type === SplitOptionType.Deposit) {
      amount = depositPrice.gross;
    }
  }

  return {
    total: amount,
    withSubtractedGiftCard: giftCard ? amount - giftCard.usedAmount : amount,
  };
}

function getStoreBookingIdKey(): string {
  const key = (window as any).RTR_ACCESS_TOKEN;
  if (!key) {
    return getStoreBookingIdKeyLegacy();
  }

  return `rentware-bookingId-${key}`;
}

function getStoreBookingIdKeyLegacy(): string {
  let postfix = '';

  const baseUrl = (window as any).RENTWARE_BASE_API_URL;
  const overwrittenHost = (window as any).RENTWARE_API_HOST_OVERWRITTEN;

  if (overwrittenHost) {
    postfix = overwrittenHost;
  } else {
    try {
      const url = new URL(baseUrl);
      postfix = url.host;
    } catch (e) {
      console.error(e);
    }
  }

  return `rentware-bookingId-${postfix}`;
}

function getStoredBookingId(): string {
  return window.localStorage.getItem(getStoreBookingIdKey());
}

function storeBookingId(id: string) {
  window.localStorage.setItem(getStoreBookingIdKey(), id);
}

function removeStoredBookingId() {
  window.localStorage.removeItem(getStoreBookingIdKey());
}

function getLocale() {
  const { locale } = getMainConfig();

  return getLocaleWithCountryCode(locale);
}
